/**
 * PartialVisibilityClassifier — 박하연 연구원 피드백(2026-05) 반영
 *
 * Face landmark가 소실됐을 때, MediaPipe PoseLandmarker가 노출하는
 * **눈 / 어깨 / 코** 3개 part의 가시성 조합으로 7가지 케이스를 분기하고
 * 각 케이스에 맞는 시선 추정 전략 + 신뢰도 + narrator 워딩을 반환한다.
 *
 * 기존 useFaceLandmarker의 face-lost 처리:
 *   - PoseLandmarker의 ear+nose 가시성으로 fallback cone 1종만 그렸음
 *   - 부분 가시성을 무시 → 노이지한 ray가 그대로 출력 ("정직하지 못한 오버레이")
 *
 * 새 정책 (4/22 메모 기반 + 0506 박하연 검수 예정):
 *   - 7 케이스로 분기
 *   - 각 케이스별 신뢰도 등급 (high/med/low/unknown) 부여
 *   - 신뢰도가 unknown 또는 low일 때는 ray 그리지 않음 — "추적 불가" 배지로 교체
 *   - narrator 출력은 워딩 중립화(feedback_ceo_wording.md)에 따라
 *     "판정/감지" 표현 ❌ → "관찰/추정" 표현 ✅
 */

// ─── Pose landmark indices (MediaPipe Pose 33-keypoint topology) ───
const POSE_LM = {
  NOSE: 0,
  LEFT_EYE: 2,
  RIGHT_EYE: 5,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
} as const;

const VISIBILITY_THRESHOLD = 0.5;

export interface PoseLandmark {
  x: number;
  y: number;
  z?: number;
  visibility?: number;
}

export type VisibilityCase =
  | 'CASE_1_FULL'           // 눈 ✓ 어깨 ✓ 코 ✓ — 정면 응시 (이상)
  | 'CASE_2_NO_NOSE'        // 눈 ✓ 어깨 ✓ 코 ✗ — 마스크 / 손이 코만 가림
  | 'CASE_3_NO_SHOULDER'    // 눈 ✓ 어깨 ✗ 코 ✓ — 어깨 가림 (책상 기댐)
  | 'CASE_4_NO_EYE'         // 눈 ✗ 어깨 ✓ 코 ✓ — 안경 반사 / 눈 감김
  | 'CASE_5_EYE_ONLY'       // 눈 ✓ 어깨 ✗ 코 ✗ — 손이 얼굴 일부 가림
  | 'CASE_6_SHOULDER_ONLY'  // 눈 ✗ 어깨 ✓ 코 ✗ — 옆모습
  | 'CASE_7_NOSE_ONLY'      // 눈 ✗ 어깨 ✗ 코 ✓ — 코끝만 보임
  | 'CASE_8_NONE';          // 모두 부재 — 추적 불가

export type Confidence = 'high' | 'med' | 'low' | 'unknown';

export interface PartialGazeEstimate {
  /** 7 케이스 + CASE_8_NONE 분류 결과 */
  case: VisibilityCase;
  /** 시선 추정 신뢰도 등급 */
  confidence: Confidence;
  /** 추정된 yaw (도, +가 우측). 추정 불가 시 null */
  yawDeg: number | null;
  /** 추정된 pitch (도, +가 위). 추정 불가 시 null */
  pitchDeg: number | null;
  /** 시선 origin 좌표 (정규화 0~1). null이면 추적 불가 */
  origin: { x: number; y: number } | null;
  /** narrator/UI에 노출할 한국어 텍스트 (워딩 중립) */
  narratorText: string;
  /** UI 배지 색상 hint */
  uiColor: 'green' | 'yellow' | 'orange' | 'red';
  /** ray 렌더링 여부 — false면 배지만 표시 */
  drawRay: boolean;
}

// ─── Internal helpers ──────────────────────────────────────────

function isVisible(lm: PoseLandmark | undefined): boolean {
  if (!lm) return false;
  return (lm.visibility ?? 1.0) >= VISIBILITY_THRESHOLD;
}

function midpoint(a: PoseLandmark, b: PoseLandmark): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * eye-shoulder 기반 head yaw 추정 (코 부재 케이스용).
 * 눈 중점이 어깨 중점에서 좌/우로 얼마나 치우쳤는지로 yaw 산출.
 */
function yawFromEyeShoulder(
  leftEye: PoseLandmark, rightEye: PoseLandmark,
  leftSh: PoseLandmark, rightSh: PoseLandmark,
): number {
  const eyeMid = midpoint(leftEye, rightEye);
  const shMid = midpoint(leftSh, rightSh);
  const shWidth = Math.max(0.01, dist(leftSh, rightSh));
  const offset = (eyeMid.x - shMid.x) / shWidth;  // -1.0 ~ 1.0 정도
  return clamp(offset * 90, -60, 60);  // 약식 환산, ±60° 클램프
}

/**
 * eye-nose 기반 yaw/pitch (어깨 부재 케이스용).
 */
function yawPitchFromEyeNose(
  leftEye: PoseLandmark, rightEye: PoseLandmark,
  nose: PoseLandmark,
): { yaw: number; pitch: number } {
  const eyeMid = midpoint(leftEye, rightEye);
  const eyeWidth = Math.max(0.01, dist(leftEye, rightEye));
  const yawFactor = (nose.x - eyeMid.x) / eyeWidth;
  const pitchFactor = (nose.y - eyeMid.y) / eyeWidth;
  return {
    yaw: clamp(yawFactor * 80, -70, 70),
    pitch: clamp(-pitchFactor * 80 + 30, -45, 45),  // nose가 eye보다 아래(정상) = pitch 0 근처
  };
}

/**
 * nose-shoulder 기반 yaw (눈 부재 케이스용 — head orientation만 추정, 시선은 추정 불가).
 */
function yawFromNoseShoulder(
  nose: PoseLandmark,
  leftSh: PoseLandmark, rightSh: PoseLandmark,
): number {
  const shMid = midpoint(leftSh, rightSh);
  const shWidth = Math.max(0.01, dist(leftSh, rightSh));
  const offset = (nose.x - shMid.x) / shWidth;
  return clamp(offset * 90, -60, 60);
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

// ─── Per-case strategies ───────────────────────────────────────

function caseFullVisibility(lm: PoseLandmark[]): PartialGazeEstimate {
  // 눈+어깨+코 모두 → eye-nose 기하학으로 정밀 추정 + shoulder cross-check
  const yp = yawPitchFromEyeNose(lm[POSE_LM.LEFT_EYE], lm[POSE_LM.RIGHT_EYE], lm[POSE_LM.NOSE]);
  return {
    case: 'CASE_1_FULL',
    confidence: 'high',
    yawDeg: yp.yaw,
    pitchDeg: yp.pitch,
    origin: { x: lm[POSE_LM.NOSE].x, y: lm[POSE_LM.NOSE].y },
    narratorText: '시선 방향 추정 중',
    uiColor: 'green',
    drawRay: true,
  };
}

function caseNoNose(lm: PoseLandmark[]): PartialGazeEstimate {
  // 마스크/손 가림 — eye + shoulder로 yaw 추정, pitch 신뢰도 낮음
  const yaw = yawFromEyeShoulder(
    lm[POSE_LM.LEFT_EYE], lm[POSE_LM.RIGHT_EYE],
    lm[POSE_LM.LEFT_SHOULDER], lm[POSE_LM.RIGHT_SHOULDER],
  );
  const eyeMid = midpoint(lm[POSE_LM.LEFT_EYE], lm[POSE_LM.RIGHT_EYE]);
  return {
    case: 'CASE_2_NO_NOSE',
    confidence: 'med',
    yawDeg: yaw,
    pitchDeg: null,  // 코 없으면 pitch 추정 어려움
    origin: eyeMid,
    narratorText: '코 영역 가림 — 시선 방향 보통 정밀도로 추정',
    uiColor: 'yellow',
    drawRay: true,
  };
}

function caseNoShoulder(lm: PoseLandmark[]): PartialGazeEstimate {
  // 어깨 가림 — eye + nose만으로 face landmark 부분판 활용
  const yp = yawPitchFromEyeNose(lm[POSE_LM.LEFT_EYE], lm[POSE_LM.RIGHT_EYE], lm[POSE_LM.NOSE]);
  return {
    case: 'CASE_3_NO_SHOULDER',
    confidence: 'med',
    yawDeg: yp.yaw,
    pitchDeg: yp.pitch,
    origin: { x: lm[POSE_LM.NOSE].x, y: lm[POSE_LM.NOSE].y },
    narratorText: '어깨 영역 가림 — 시선 방향 추정 중',
    uiColor: 'yellow',
    drawRay: true,
  };
}

function caseNoEye(lm: PoseLandmark[]): PartialGazeEstimate {
  // 눈 가림 — head orientation만 추정 가능, 시선 정밀도 낮음
  const yaw = yawFromNoseShoulder(
    lm[POSE_LM.NOSE],
    lm[POSE_LM.LEFT_SHOULDER], lm[POSE_LM.RIGHT_SHOULDER],
  );
  return {
    case: 'CASE_4_NO_EYE',
    confidence: 'low',
    yawDeg: yaw,
    pitchDeg: null,
    origin: { x: lm[POSE_LM.NOSE].x, y: lm[POSE_LM.NOSE].y },
    narratorText: '눈 영역 일시 가림 — 헤드 포즈 기반 약식 추정',
    uiColor: 'orange',
    drawRay: true,
  };
}

function caseEyeOnly(lm: PoseLandmark[]): PartialGazeEstimate {
  // 눈만 보임 — 방향 정보 매우 제한적, 보수적 출력
  const eyeMid = midpoint(lm[POSE_LM.LEFT_EYE], lm[POSE_LM.RIGHT_EYE]);
  return {
    case: 'CASE_5_EYE_ONLY',
    confidence: 'low',
    yawDeg: null,
    pitchDeg: null,
    origin: eyeMid,
    narratorText: '얼굴 부분 가림 — 시선 방향 추적 정밀도 낮음',
    uiColor: 'orange',
    drawRay: false,  // ray 그리지 않음 — 신뢰도 부족
  };
}

function caseShoulderOnly(lm: PoseLandmark[], shoulderAngle: number): PartialGazeEstimate {
  // 어깨만 — 측면 자세 감지, 시선 추적 불가
  // shoulderAngle로 측면 여부 추정 (90° 가까우면 측면)
  return {
    case: 'CASE_6_SHOULDER_ONLY',
    confidence: 'unknown',
    yawDeg: null,
    pitchDeg: null,
    origin: midpoint(lm[POSE_LM.LEFT_SHOULDER], lm[POSE_LM.RIGHT_SHOULDER]),
    narratorText: Math.abs(shoulderAngle) > 30
      ? '측면 자세 — 시선 방향 추적 불가'
      : '얼굴 영역 추적 불가',
    uiColor: 'red',
    drawRay: false,
  };
}

function caseNoseOnly(lm: PoseLandmark[]): PartialGazeEstimate {
  // 코끝만 — 단일 점, 방향 추정 불가
  return {
    case: 'CASE_7_NOSE_ONLY',
    confidence: 'unknown',
    yawDeg: null,
    pitchDeg: null,
    origin: { x: lm[POSE_LM.NOSE].x, y: lm[POSE_LM.NOSE].y },
    narratorText: '얼굴 가림 정도 큼 — 시선 추적 불가',
    uiColor: 'red',
    drawRay: false,
  };
}

const CASE_NONE_RESULT: PartialGazeEstimate = {
  case: 'CASE_8_NONE',
  confidence: 'unknown',
  yawDeg: null,
  pitchDeg: null,
  origin: null,
  narratorText: '헤드 포즈 추적 불가',
  uiColor: 'red',
  drawRay: false,
};

// ─── Public API ────────────────────────────────────────────────

export interface ClassifierInput {
  /** MediaPipe PoseLandmarker landmarks (33-keypoint), 또는 null */
  poseLandmarks: PoseLandmark[] | null;
  /** 어깨 각도 (도) — 측면 판정 보조 */
  shoulderAngle: number;
}

export function classifyPartialVisibility(input: ClassifierInput): PartialGazeEstimate {
  const lm = input.poseLandmarks;
  if (!lm || lm.length < 13) {
    return CASE_NONE_RESULT;
  }

  const eyeVisible =
    isVisible(lm[POSE_LM.LEFT_EYE]) || isVisible(lm[POSE_LM.RIGHT_EYE]);
  const shoulderVisible =
    isVisible(lm[POSE_LM.LEFT_SHOULDER]) && isVisible(lm[POSE_LM.RIGHT_SHOULDER]);
  const noseVisible = isVisible(lm[POSE_LM.NOSE]);

  // 3-bit key: eye/shoulder/nose
  const key = `${eyeVisible ? 1 : 0}${shoulderVisible ? 1 : 0}${noseVisible ? 1 : 0}`;
  switch (key) {
    case '111': return caseFullVisibility(lm);
    case '110': return caseNoNose(lm);
    case '101': return caseNoShoulder(lm);
    case '011': return caseNoEye(lm);
    case '100': return caseEyeOnly(lm);
    case '010': return caseShoulderOnly(lm, input.shoulderAngle);
    case '001': return caseNoseOnly(lm);
    case '000':
    default:    return CASE_NONE_RESULT;
  }
}
